<template>
  <v-container fluid>
    <v-row align="center" class="pb-2">
      <h1 class="headline">{{ dataTypeName }}</h1>
      <v-spacer></v-spacer>

      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create {{ dataTypeName }} Record</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items-per-page="5"
          class="elevation-1"
          :items="items"
        >
          <template
            v-for="(d, i) in dateTypeAttributes"
            v-slot:[`item.${d}`]="{ item }"
          >
            {{ item[d] | formatDate }}
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hideView
              @editItem="onEditItem"
              @deleteItem="onDeleteItem"
            ></default-list-actions>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row justify="center">
      <download-csv :data="csvData" delimiter=",">
        <v-btn class="mr-1" :disabled="items.length == 0"
          ><v-icon left small>mdi-file-delimited</v-icon> Get CSV</v-btn
        >
      </download-csv>
    </v-row>
    <custom-data-record-edit-dialog
      v-model="editedRecord"
      :show="showRecordEditor"
      @close="showRecordEditor = false"
      @input="onSave"
      :custom-data-type="customDataType"
    />
    <v-row v-if="debug">
      <v-col>
        <code>{{ csvData }}</code>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import CustomDataRecordEditDialog from "./CustomDataRecordEditDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
import DefaultListActions from "../DefaultListActions.vue";
const CDATA_TYPE_DETAILS = gql`
  query customDataTypeDetails($id: ID!) {
    customDataType(id: $id) {
      id
      name
      description
      attributes {
        edges {
          node {
            id
            name
            description
            type
            order
          }
        }
      }
    }
  }
`;
const CDATA_RECORDS = gql`
  query customDataTypeRecords($ownerId: ID, $customDataTypeId: ID) {
    customDataTypeRecords(
      filter: { ownerId_eq: $ownerId, customDataTypeId_eq: $customDataTypeId }
    ) {
      edges {
        node {
          id
          attributes {
            edges {
              node {
                id
                customDataTypeAttributeId
                content
              }
            }
          }
        }
      }
    }
  }
`;
const CREATE_RECORD = gql`
  mutation createCDrecord($input: CustomDataTypeRecordInput) {
    createCustomDataTypeRecord(input: $input) {
      id
      attributes {
        edges {
          node {
            id
            customDataTypeAttributeId
            content
          }
        }
      }
    }
  }
`;
const UPDATE_RECORD = gql`
  mutation updateCDrecord($input: CustomDataTypeRecordInput) {
    updateCustomDataTypeRecord(input: $input) {
      id
      attributes {
        edges {
          node {
            id
            customDataTypeAttributeId
            content
          }
        }
      }
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteCDrecord($id: ID!) {
    deleteCustomDataTypeRecord(id: $id) {
      id
    }
  }
`;
export default {
  components: { CustomDataRecordEditDialog, DefaultListActions },
  name: "custom-data-types-records",
  props: {
    ownerId: {
      type: String,
      default: () => "",
    },
    customDataTypeId: {
      type: String,
      default: () => "",
    },
  },

  data() {
    return {
      debug: false,
      editedRecord: null,
      showRecordEditor: false,
    };
  },

  computed: {
    csvData() {
      return this.items.map((item) => {
        const obj = {};
        this.headers.forEach((header) => {
          if (header.value != "actions") {
            if (this.dateTypeAttributes.indexOf(header.value) > -1) {
              obj[header.text] = this.$options.filters.formatDate(
                item[header.value]
              );
            } else {
              obj[header.text] = item[header.value];
            }
          }
        });
        return obj;
      });
    },
    dataTypeName() {
      return this.customDataType ? this.customDataType.name : "";
    },
    dateTypeAttributes() {
      return this.customDataType && this.customDataType.attributes
        ? this.customDataType.attributes.edges
            .filter((attr) => attr.node.type == "DATE")
            .map((attr) => attr.node.id)
        : [];
    },
    headers() {
      return this.customDataType && this.customDataType.attributes
        ? [
            ...this.customDataType.attributes.edges.map((attr) => {
              return {
                text: attr.node.name,
                value: attr.node.id,
              };
            }),
            { text: "Actions", value: "actions", sortable: false },
          ]
        : [];
    },
    items() {
      return this.customDataTypeRecords
        ? this.customDataTypeRecords.edges
            .map(removeGQLTypename)
            .map((record) => {
              return {
                id: record.id,
                ...record.attributes.edges.reduce((acc, attr) => {
                  acc[attr.node.customDataTypeAttributeId] = attr.node.content;
                  return acc;
                }, {}),
              };
            })
        : [];
    },
  },

  methods: {
    onCreateNew() {
      this.editedRecord = null;
      this.showRecordEditor = true;
    },
    onSave(payload) {
      this.showRecordEditor = false;
      const mutation = payload.id ? UPDATE_RECORD : CREATE_RECORD;
      this.$apollo
        .mutate({
          mutation,
          variables: {
            input: {
              ...payload,
              ownerId: this.ownerId,
              customDataTypeId: this.customDataTypeId,
            },
          },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Record Saved.",
          });
          this.$apollo.queries.customDataTypeRecords.refetch();
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onEditItem(item) {
      this.editedRecord = item;
      this.showRecordEditor = true;
    },
    onDeleteItem(item) {
      console.log("Delete", item);
      this.$apollo
        .mutate({
          mutation: DELETE_MUTATION,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: CDATA_RECORDS,
              variables: {
                ownerId: this.ownerId,
                customDataTypeId: this.customDataTypeId,
              },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Record Deleted.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  apollo: {
    customDataType: {
      query: CDATA_TYPE_DETAILS,
      variables() {
        return {
          id: this.customDataTypeId,
        };
      },
      skip() {
        return this.customDataType == "";
      },
      fetchPolicy: "network-only",
    },
    customDataTypeRecords: {
      query: CDATA_RECORDS,
      variables() {
        return {
          ownerId: this.ownerId,
          customDataTypeId: this.customDataTypeId,
        };
      },
      skip() {
        return this.ownerId == "";
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/gt/shared/custom-data-types/CustomDataTypeRecords.vue